import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [status, setStatus] = useState('draft');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${id}`);
      const post = response.data;
      setTitle(post.title);
      setExcerpt(post.excerpt || '');
      setContent(post.content);
      setFeaturedImage(post.featured_image || '');
      setStatus(post.status);
      setTags(post.tags);
      setCategories(post.categories);
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Failed to load post');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddCategory = () => {
    if (categoryInput.trim() && !categories.includes(categoryInput.trim())) {
      setCategories([...categories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (categoryToRemove) => {
    setCategories(categories.filter(cat => cat !== categoryToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const postData = {
      title,
      excerpt: excerpt || null,
      content,
      featured_image: featuredImage || null,
      status,
      tags,
      categories,
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/posts/${id}`, postData);
        toast.success('Post updated successfully');
      } else {
        await axios.post(`${API}/posts`, postData);
        toast.success('Post created successfully');
      }
      navigate('/admin');
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16" data-testid="post-editor">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="-ml-4 mb-4 text-zinc-400 hover:text-emerald-400"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="mr-2 w-4 h-4" /> Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold" data-testid="editor-title">
            {isEditing ? 'Edit Post' : 'Create New Post'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="glass border-zinc-800 p-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title" className="text-zinc-300">Title *</Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter post title"
                      required
                      className="bg-zinc-900 border-zinc-800 mt-1 text-zinc-100"
                      data-testid="title-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="excerpt" className="text-zinc-300">Excerpt</Label>
                    <Textarea
                      id="excerpt"
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      placeholder="Brief summary of your post"
                      rows={3}
                      className="bg-zinc-900 border-zinc-800 mt-1 text-zinc-100"
                      data-testid="excerpt-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="content" className="text-zinc-300">Content *</Label>
                    <Textarea
                      id="content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Write your post content here..."
                      rows={15}
                      required
                      className="bg-zinc-900 border-zinc-800 mt-1 font-code text-zinc-100"
                      data-testid="content-input"
                    />
                  </div>
                </div>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Publish Settings */}
              <Card className="glass border-zinc-800 p-6">
                <h3 className="font-semibold mb-4">Publish Settings</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-300 mb-2 block">Status</Label>
                    <div className="space-y-2">
                      <label className="flex items-center text-zinc-100">
                        <input
                          type="radio"
                          value="draft"
                          checked={status === 'draft'}
                          onChange={(e) => setStatus(e.target.value)}
                          className="mr-2 text-zinc-100"
                          data-testid="status-draft"
                        />
                        <span className="text-sm">Draft</span>
                      </label>
                      <label className="flex items-center text-zinc-100">
                        <input
                          type="radio"
                          value="published"
                          checked={status === 'published'}
                          onChange={(e) => setStatus(e.target.value)}
                          className="mr-2 text-zinc-100"
                          data-testid="status-published"
                        />
                        <span className="text-sm">Published</span>
                      </label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                    disabled={loading}
                    data-testid="save-post-btn"
                  >
                    <Save className="mr-2 w-4 h-4" />
                    {loading ? 'Saving...' : (isEditing ? 'Update Post' : 'Create Post')}
                  </Button>
                </div>
              </Card>

              {/* Featured Image */}
              <Card className="glass border-zinc-800 p-6">
                <h3 className="font-semibold mb-4">Featured Image</h3>
                <Input
                  value={featuredImage}
                  onChange={(e) => setFeaturedImage(e.target.value)}
                  placeholder="Image URL"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100"
                  data-testid="featured-image-input"
                />
                {featuredImage && (
                  <div className="mt-4">
                    <img
                      src={featuredImage}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </Card>

              {/* Tags */}
              <Card className="glass border-zinc-800 p-6">
                <h3 className="font-semibold mb-4">Tags</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="tag-input"
                  />
                  <Button type="button" onClick={handleAddTag} variant="outline" className="border-zinc-700" data-testid="add-tag-btn">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, i) => (
                    <Badge
                      key={i}
                      className="bg-emerald-500/10 text-emerald-400 cursor-pointer hover:bg-red-500/20"
                      onClick={() => handleRemoveTag(tag)}
                      data-testid={`tag-${i}`}
                    >
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              </Card>

              {/* Categories */}
              <Card className="glass border-zinc-800 p-6">
                <h3 className="font-semibold mb-4">Categories</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
                    placeholder="Add category"
                    className="bg-zinc-900 border-zinc-800"
                    data-testid="category-input"
                  />
                  <Button type="button" onClick={handleAddCategory} variant="outline" className="border-zinc-700" data-testid="add-category-btn">
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat, i) => (
                    <Badge
                      key={i}
                      className="bg-cyan-500/10 text-cyan-400 cursor-pointer hover:bg-red-500/20"
                      onClick={() => handleRemoveCategory(cat)}
                      data-testid={`category-${i}`}
                    >
                      {cat} ×
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPostEditor;
